/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Table filter component
 *
 * @author Vlaidslav Oshkanov
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */
import { Fragment, memo } from 'react';
import type { ReactNode, MouseEvent } from 'react';

import classNames from 'classnames';

import { ValueOf } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { EllipseText } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import type { IOptionItemProps } from '@components/common/Select/Select';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';

import type { IFilterDateParams } from './FilterDate';
import type { IFilterRangeParams } from './FilterRange';
import {
    FilterColorCircleStyled,
    FilterTextStyled,
    FilterTitleStyled,
    FilterTitleTextStyled,
    FilterTitleTextWithIconStyled,
} from './styled/FilterStyled';

export type IOnFilterChangeParams =
    IOptionItemProps[]
    | IOptionItemProps
    | IFilterDateParams
    | IFilterRangeParams
    | { valueFrom: string; valueTo: string }
    | string
    | number
    | undefined;

export interface ISortEnum {
    ascending: string;
    default: string;
    descending: string;
}

export const SortEnum: ISortEnum = {
    ascending: 'ASC',
    default: 'DEFAULT',
    descending: 'DESC',
};

/**
 * @description Filter sort
 * @interface IFilterSort
 * @property {string} field - field name
 * @property {SortEnum} direction - sort direction
 */

export interface IFilterSort {
    direction?: ValueOf<ISortEnum>;
    property?: string;
}

/**
 * @description Filter props
 * @interface IFilterProps
 * @property {string} name - filter name
 * @property {string} sort - sort field name
 * @property {() => void} onSort - sort method
 */
export interface IFilterProps {
    children?: ReactNode;
    headerPointColor?: string;
    isSortable?: boolean;
    name: string;
    onSort: () => void;
    sort: string | undefined;
    specSign: string;
    tooltipMsg?: string;
}

/**
 * @description Check is filter active (has asc or desc sort)
 * @param {string | undefined} sort
 * @return {boolean}
 */
const isFilterActive = (sort: string | undefined): boolean =>
    sort === SortEnum.ascending || sort === SortEnum.descending;

/**
 * Common class for filters, contains title, historyOrder icon. Receives Select, Multiselect of DropDownFilter as
 * it's child.
 *
 * @property {string} name filter name.
 * @property {string} sort sort direction.
 */
const Filter = (props: IFilterProps) => {
    const { name, sort, onSort, children, specSign, tooltipMsg, isSortable, headerPointColor } = props;
    const clickHandler = (e: MouseEvent<HTMLDivElement>): void => {
        if (e.currentTarget.contains(e.target as Node) && isSortable) {
            onSort();
        }
    };
    const filterClasses = classNames('filter', {
        active: isFilterActive(sort),
        sortable: props.isSortable,
    });
    return (
        <Fragment>
            <FilterTitleStyled
                className={filterClasses}
                onClick={clickHandler}
            >
                {
                    headerPointColor !== undefined &&
                    <FilterColorCircleStyled color={props.headerPointColor} />
                }
                {
                    name && (
                        isSortable || !tooltipMsg
                            ? <EllipseText title={name} tooltipMsg={tooltipMsg} wrapper={FilterTitleTextStyled}>
                                {name + specSign}
                            </EllipseText>
                            : <ArrowTooltip message={tooltipMsg} placement="top" title={name}>
                                <FilterTextStyled>
                                    <EllipseText title={name} wrapper={FilterTitleTextWithIconStyled}>
                                        {name + specSign}
                                    </EllipseText>
                                    {
                                        tooltipMsg &&
                                        <ImgStyled
                                            className="filter__title-icon small"
                                            src={icons.infoBlueIcon}
                                        />
                                    }
                                </FilterTextStyled>
                            </ArrowTooltip>
                    )
                }
                {
                    isSortable &&
                    <ImgStyled
                        className='small'
                        src={(sort === SortEnum.ascending)
                            ? icons.arrowSortAsc
                            : (sort === SortEnum.descending)
                                ? icons.arrowSortDesc
                                : icons.arrowSortDefault}
                    />
                }
            </FilterTitleStyled>
            {children}
        </Fragment>
    );
};
Filter.defaultProps = {
    name: '',
    specSign: '',
};

export default memo(Filter);
